import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, ListGroup, Badge } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import api from '../../services/api';

const Profile = ({ embedded = false }) => {
  const { user, updateProfile, refreshProfile } = useAuth();
  const { t } = useLanguage();
  const [editMode, setEditMode] = useState(false);
  const [editAddressMode, setEditAddressMode] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    wechat_id: user?.wechat_id || '',
    telegram_username: user?.telegram_username || '',
  });
  const [addressData, setAddressData] = useState({
    delivery_address_street: user?.delivery_address_street || '',
    delivery_address_city: user?.delivery_address_city || '',
    delivery_address_postal_code: user?.delivery_address_postal_code || '',
    delivery_address_country: user?.delivery_address_country || '',
    delivery_address_phone: user?.delivery_address_phone || '',
  });
  const [loading, setLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [addressMessage, setAddressMessage] = useState('');
  const [addressError, setAddressError] = useState('');
  
  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docError, setDocError] = useState('');
  const [docMessage, setDocMessage] = useState('');
  const fileInputRef = useRef(null);
  
  // Legal documents from user profile
  const [documents, setDocuments] = useState(user?.legal_documents || []);

  // Per-store: automatic return verification (needs_check default for new items)
  const [returnCheckStores, setReturnCheckStores] = useState([]);
  const [returnCheckLoading, setReturnCheckLoading] = useState(false);
  const [returnCheckError, setReturnCheckError] = useState('');
  const [returnCheckToggleId, setReturnCheckToggleId] = useState(null);

  useEffect(() => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      wechat_id: user?.wechat_id || '',
      telegram_username: user?.telegram_username || '',
    });
  }, [user?.first_name, user?.last_name, user?.wechat_id, user?.telegram_username]);

  useEffect(() => {
    setDocuments(user?.legal_documents || []);
    setAddressData({
      delivery_address_street: user?.delivery_address_street || '',
      delivery_address_city: user?.delivery_address_city || '',
      delivery_address_postal_code: user?.delivery_address_postal_code || '',
      delivery_address_country: user?.delivery_address_country || '',
      delivery_address_phone: user?.delivery_address_phone || '',
    });
  }, [user?.legal_documents, user?.delivery_address_street, user?.delivery_address_city, 
      user?.delivery_address_postal_code, user?.delivery_address_country, user?.delivery_address_phone]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setReturnCheckLoading(true);
      setReturnCheckError('');
      try {
        const data = await api.getStores();
        const list = Array.isArray(data) ? data : data?.results || [];
        if (!cancelled) setReturnCheckStores(list);
      } catch {
        if (!cancelled) setReturnCheckError(t('profile.autoReturnCheckLoadError'));
      } finally {
        if (!cancelled) setReturnCheckLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleAutoReturnCheckChange = async (store, enabled) => {
    setReturnCheckToggleId(store.id);
    setReturnCheckError('');
    try {
      await api.updateStore(store.id, { auto_return_check_enabled: enabled });
      setReturnCheckStores((prev) =>
        prev.map((s) =>
          s.id === store.id ? { ...s, auto_return_check_enabled: enabled } : s,
        ),
      );
    } catch (err) {
      const errorText = err.data?.detail || err.data?.errorText;
      if (typeof errorText === 'string') {
        setReturnCheckError(errorText);
      } else if (typeof errorText === 'object') {
        setReturnCheckError(Object.values(errorText).flat().join(' '));
      } else {
        setReturnCheckError(t('profile.autoReturnCheckUpdateError'));
      }
    } finally {
      setReturnCheckToggleId(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    setMessage('');
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await updateProfile(formData);
      setMessage(t('profile.updateSuccess'));
      setEditMode(false);
    } catch (err) {
      const errorText = err.data?.errorText;
      if (typeof errorText === 'string') {
        setError(errorText);
      } else if (typeof errorText === 'object') {
        const messages = Object.values(errorText).flat();
        setError(messages.join(' '));
      } else {
        setError(t('profile.updateError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      wechat_id: user?.wechat_id || '',
      telegram_username: user?.telegram_username || '',
    });
    setEditMode(false);
    setError('');
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setAddressError('');
    setAddressMessage('');
  };

  const handleSaveAddress = async () => {
    setAddressLoading(true);
    setAddressError('');
    setAddressMessage('');

    try {
      await updateProfile(addressData);
      setAddressMessage(t('profile.addressUpdateSuccess'));
      setEditAddressMode(false);
    } catch (err) {
      const errorText = err.data?.errorText;
      if (typeof errorText === 'string') {
        setAddressError(errorText);
      } else if (typeof errorText === 'object') {
        const messages = Object.values(errorText).flat();
        setAddressError(messages.join(' '));
      } else {
        setAddressError(t('profile.updateError'));
      }
    } finally {
      setAddressLoading(false);
    }
  };

  const handleCancelAddressEdit = () => {
    setAddressData({
      delivery_address_street: user?.delivery_address_street || '',
      delivery_address_city: user?.delivery_address_city || '',
      delivery_address_postal_code: user?.delivery_address_postal_code || '',
      delivery_address_country: user?.delivery_address_country || '',
      delivery_address_phone: user?.delivery_address_phone || '',
    });
    setEditAddressMode(false);
    setAddressError('');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setDocError(t('profile.documentsOnlyPdf'));
      return;
    }
    
    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setDocError(t('profile.documentsMaxSize'));
      return;
    }
    
    setUploadingDoc(true);
    setDocError('');
    setDocMessage('');
    
    try {
      const response = await api.uploadDocument(file);
      if (response.data) {
        setDocuments(prev => [response.data, ...prev]);
        setDocMessage(t('profile.documentsUploadSuccess'));
        // Refresh profile to get updated documents list
        if (refreshProfile) {
          await refreshProfile();
        }
      }
    } catch (err) {
      const errorText = err.data?.errorText;
      if (typeof errorText === 'string') {
        setDocError(errorText);
      } else if (typeof errorText === 'object' && errorText.file) {
        setDocError(Array.isArray(errorText.file) ? errorText.file[0] : errorText.file);
      } else {
        setDocError(t('profile.documentsUploadError'));
      }
    } finally {
      setUploadingDoc(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm(t('profile.documentsConfirmDelete'))) {
      return;
    }
    
    try {
      await api.deleteDocument(docId);
      setDocuments(prev => prev.filter(doc => doc.id !== docId));
      setDocMessage(t('profile.documentsDeleteSuccess'));
      // Refresh profile to get updated documents list
      if (refreshProfile) {
        await refreshProfile();
      }
    } catch (err) {
      setDocError(t('profile.documentsDeleteError'));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const Shell = embedded ? 'div' : Container;
  const shellProps = embedded
    ? { className: 'chat-app-profile-embed' }
    : { className: 'py-4 content-container' };

  return (
    <Shell {...shellProps}>
      <Row className={embedded ? '' : 'justify-content-center'}>
        <Col lg={embedded ? 12 : 8}>
          {/* Profile Card */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
              <h5 className="mb-0 fw-bold">{t('profile.title')}</h5>
              {!editMode && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setEditMode(true)}
                >
                  <i className="bi bi-pencil me-2"></i>
                  {t('profile.edit')}
                </Button>
              )}
            </Card.Header>
            <Card.Body className="p-4">
              {message && <Alert variant="success">{message}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small">
                    {t('profile.email')}
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-light"
                  />
                  <Form.Text className="text-muted">
                    {t('profile.emailCannotChange')}
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small">
                        {t('profile.firstName')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        disabled={!editMode}
                        className={!editMode ? 'bg-light' : ''}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small">
                        {t('profile.lastName')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        disabled={!editMode}
                        className={!editMode ? 'bg-light' : ''}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <p className="text-muted small mb-2">{t('profile.messengerHint')}</p>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small">
                        {t('profile.wechatId')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="wechat_id"
                        value={formData.wechat_id}
                        onChange={handleChange}
                        disabled={!editMode}
                        className={!editMode ? 'bg-light' : ''}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small">
                        {t('profile.telegramUsername')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="telegram_username"
                        value={formData.telegram_username}
                        onChange={handleChange}
                        disabled={!editMode}
                        className={!editMode ? 'bg-light' : ''}
                        placeholder="@username"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small">
                    {t('profile.accountStatus')}
                  </Form.Label>
                  <div>
                    <span className="badge bg-success">
                      <i className="bi bi-check-circle me-1"></i>
                      {t('profile.emailVerified')}
                    </span>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small">
                    {t('profile.memberSince')}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      user?.date_joined
                        ? new Date(user.date_joined).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : '-'
                    }
                    disabled
                    className="bg-light"
                  />
                </Form.Group>

                {editMode && (
                  <div className="d-flex gap-2 justify-content-end mt-4 pt-3 border-top">
                    <Button
                      variant="outline-secondary"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      {t('profile.cancel')}
                    </Button>
                    <Button
                      className="btn-orange"
                      onClick={handleSaveProfile}
                      disabled={loading}
                    >
                      {loading ? t('profile.saving') : t('profile.save')}
                    </Button>
                  </div>
                )}
              </Form>
            </Card.Body>
          </Card>

          {/* Delivery Address Card */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
              <h5 className="mb-0 fw-bold">
                <i className="bi bi-geo-alt me-2"></i>
                {t('profile.deliveryAddressTitle')}
              </h5>
              {!editAddressMode && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setEditAddressMode(true)}
                >
                  <i className="bi bi-pencil me-2"></i>
                  {t('profile.edit')}
                </Button>
              )}
            </Card.Header>
            <Card.Body className="p-4">
              {addressMessage && <Alert variant="success">{addressMessage}</Alert>}
              {addressError && <Alert variant="danger">{addressError}</Alert>}
              
              {!user?.has_delivery_address && !editAddressMode && (
                <Alert variant="warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {t('profile.deliveryAddressWarning')}
                </Alert>
              )}

              <p className="text-muted small mb-3">
                {t('profile.deliveryAddressDescription')}
              </p>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted small">
                    {t('profile.addressStreet')}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="delivery_address_street"
                    value={addressData.delivery_address_street}
                    onChange={handleAddressChange}
                    disabled={!editAddressMode}
                    className={!editAddressMode ? 'bg-light' : ''}
                    placeholder={t('profile.addressStreetPlaceholder')}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small">
                        {t('profile.addressCity')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="delivery_address_city"
                        value={addressData.delivery_address_city}
                        onChange={handleAddressChange}
                        disabled={!editAddressMode}
                        className={!editAddressMode ? 'bg-light' : ''}
                        placeholder={t('profile.addressCityPlaceholder')}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small">
                        {t('profile.addressPostalCode')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="delivery_address_postal_code"
                        value={addressData.delivery_address_postal_code}
                        onChange={handleAddressChange}
                        disabled={!editAddressMode}
                        className={!editAddressMode ? 'bg-light' : ''}
                        placeholder={t('profile.addressPostalCodePlaceholder')}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small">
                        {t('profile.addressCountry')}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="delivery_address_country"
                        value={addressData.delivery_address_country}
                        onChange={handleAddressChange}
                        disabled={!editAddressMode}
                        className={!editAddressMode ? 'bg-light' : ''}
                        placeholder={t('profile.addressCountryPlaceholder')}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small">
                        {t('profile.addressPhone')}
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        name="delivery_address_phone"
                        value={addressData.delivery_address_phone}
                        onChange={handleAddressChange}
                        disabled={!editAddressMode}
                        className={!editAddressMode ? 'bg-light' : ''}
                        placeholder={t('profile.addressPhonePlaceholder')}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {editAddressMode && (
                  <div className="d-flex gap-2 justify-content-end mt-4 pt-3 border-top">
                    <Button
                      variant="outline-secondary"
                      onClick={handleCancelAddressEdit}
                      disabled={addressLoading}
                    >
                      {t('profile.cancel')}
                    </Button>
                    <Button
                      className="btn-orange"
                      onClick={handleSaveAddress}
                      disabled={addressLoading}
                    >
                      {addressLoading ? t('profile.saving') : t('profile.save')}
                    </Button>
                  </div>
                )}
              </Form>
            </Card.Body>
          </Card>

          {/* Automatic return check — per store */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white border-bottom py-3">
              <h5 className="mb-0 fw-bold">
                <i className="bi bi-shield-check me-2"></i>
                {t('profile.autoReturnCheckTitle')}
              </h5>
            </Card.Header>
            <Card.Body className="p-4">
              <p className="text-muted small mb-3">{t('profile.autoReturnCheckHint')}</p>
              {returnCheckError && (
                <Alert variant="danger" dismissible onClose={() => setReturnCheckError('')}>
                  {returnCheckError}
                </Alert>
              )}
              {returnCheckLoading ? (
                <div className="text-center py-3 text-muted">
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('profile.saving')}
                </div>
              ) : returnCheckStores.length === 0 ? (
                <p className="text-muted small mb-0">{t('profile.autoReturnCheckNoStores')}</p>
              ) : (
                <ListGroup variant="flush">
                  {returnCheckStores.map((store) => {
                    const on =
                      store.auto_return_check_enabled !== false &&
                      store.auto_return_check_enabled !== 'false';
                    return (
                      <ListGroup.Item
                        key={store.id}
                        className="px-0 d-flex justify-content-between align-items-center flex-wrap gap-2"
                      >
                        <div>
                          <div className="fw-medium">{store.name || `Store ${store.store_id}`}</div>
                          <small className="text-muted">
                            {t('stores.storeId')}: {store.store_id}
                          </small>
                        </div>
                        <Form.Check
                          type="switch"
                          id={`auto-return-check-${store.id}`}
                          label={on ? t('common.yes') : t('common.no')}
                          checked={on}
                          disabled={returnCheckToggleId === store.id}
                          onChange={(e) =>
                            handleAutoReturnCheckChange(store, e.target.checked)
                          }
                        />
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          {/* Legal Documents Card */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
              <h5 className="mb-0 fw-bold">
                <i className="bi bi-file-earmark-pdf me-2"></i>
                {t('profile.documentsTitle')}
              </h5>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf"
                  style={{ display: 'none' }}
                />
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingDoc}
                >
                  {uploadingDoc ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('profile.documentsUploading')}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-2"></i>
                      {t('profile.documentsUpload')}
                    </>
                  )}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              {docMessage && <Alert variant="success" dismissible onClose={() => setDocMessage('')}>{docMessage}</Alert>}
              {docError && <Alert variant="danger" dismissible onClose={() => setDocError('')}>{docError}</Alert>}

              <p className="text-muted small mb-3">
                {t('profile.documentsDescription')}
              </p>

              {documents.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-file-earmark-pdf fs-1 mb-2 d-block opacity-50"></i>
                  <p className="mb-0">{t('profile.documentsEmpty')}</p>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {documents.map((doc) => (
                    <ListGroup.Item 
                      key={doc.id} 
                      className="d-flex justify-content-between align-items-center px-3 py-3"
                    >
                      <div className="d-flex align-items-center">
                        <div 
                          className="bg-danger bg-opacity-10 rounded d-flex align-items-center justify-content-center me-3"
                          style={{ width: '56px', height: '56px' }}
                        >
                          <i className="bi bi-file-earmark-pdf text-danger" style={{ fontSize: '1.75rem' }}></i>
                        </div>
                        <div>
                          <div className="fw-medium">{doc.original_filename}</div>
                          <small className="text-muted">
                            {doc.file_size_display || formatFileSize(doc.file_size)}
                            {doc.uploaded_at && (
                              <span className="ms-2">
                                • {new Date(doc.uploaded_at).toLocaleDateString()}
                              </span>
                            )}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        {doc.file_url && (
                          <Button
                            variant="outline-secondary"
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-flex align-items-center justify-content-center"
                            style={{ width: '44px', height: '44px', padding: 0 }}
                          >
                            <i className="bi bi-download" style={{ fontSize: '1.25rem' }}></i>
                          </Button>
                        )}
                        <Button
                          variant="outline-danger"
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="d-flex align-items-center justify-content-center"
                          style={{ width: '44px', height: '44px', padding: 0 }}
                        >
                          <i className="bi bi-trash" style={{ fontSize: '1.25rem' }}></i>
                        </Button>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Shell>
  );
};

export default Profile;
